import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  useGetUserClothingQuery,
  useUploadClothingMutation,
  useDeleteClothingMutation,
  useUpdateClothingMutation
} from '../redux/apis/endpoints/closetApi';
import {
  addToTryOn,
  removeFromTryOn,
  clearTryOnItems,
  setActiveItems,
  setFilters,
  setSearchTerm,
  setSortBy,
  setCategoryFilter,
  toggleMultiSelectMode,
  toggleItemSelection,
  selectAllItems,
  clearSelections,
  cleanupDeletedItems,
  selectSelectedForTryOn,
  selectActiveItems,
  selectHasActiveItems,
  selectClothingFilters,
  selectMultiSelectMode,
  selectSelectedItemIds,
  selectIsItemSelectedForTryOn,
  selectIsItemSelected
} from '../redux/slices/clothingItemsSlice';

/**
 * Custom hook that combines RTK Query clothing data with UI state management
 * Provides a unified interface for clothing items operations
 */
export const useClothingItems = () => {
  const dispatch = useDispatch();
  
  // Get UI state from slice
  const selectedForTryOn = useSelector(selectSelectedForTryOn);
  const activeItems = useSelector(selectActiveItems);
  const hasActiveItems = useSelector(selectHasActiveItems);
  const filters = useSelector(selectClothingFilters);
  const multiSelectMode = useSelector(selectMultiSelectMode);
  const selectedItemIds = useSelector(selectSelectedItemIds);
  
  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('token');
  
  // State for guest clothing items
  const [guestClothingItems, setGuestClothingItems] = useState([]);
  
  // Load guest clothes from localStorage
  useEffect(() => {
    if (!isLoggedIn) {
      const items = JSON.parse(localStorage.getItem('guestClothes') || '[]');
      setGuestClothingItems(items);
      // console.log('🔍 Loaded guest clothes from localStorage:', items.length, 'items');
    }
  }, [isLoggedIn]);
  
  // Get clothing data from RTK Query
  const {
    data: backendClothingItems = [],
    isLoading: isLoadingBackend,
    error,
    refetch
  } = useGetUserClothingQuery(undefined, {
    skip: !isLoggedIn // Skip query if not logged in
  });
  
  // Use appropriate data source
  const clothingItems = isLoggedIn ? backendClothingItems : guestClothingItems;
  const isLoading = isLoggedIn ? isLoadingBackend : false;
  
  // console.log('🔍 useClothingItems:', { isLoggedIn, itemCount: clothingItems.length });
  
  // RTK Query mutations
  const [uploadClothing, { isLoading: isUploading }] = useUploadClothingMutation({
    fixedCacheKey: 'shared-upload-clothing'
  });
  const [deleteClothing, { isLoading: isDeleting }] = useDeleteClothingMutation();
  const [updateClothing, { isLoading: isUpdating }] = useUpdateClothingMutation();
  
  // UI actions
  const handleAddToTryOn = (item) => {
    dispatch(addToTryOn(item));
  };
  
  const handleRemoveFromTryOn = (itemId) => {
    dispatch(removeFromTryOn(itemId));
  };
  
  const handleClearTryOnItems = () => {
    dispatch(clearTryOnItems());
  };
  
  const handleSetActiveItems = (items) => {
    dispatch(setActiveItems(items));
  };
  
  // Filter actions
  const handleSetFilters = (newFilters) => {
    dispatch(setFilters(newFilters));
  };
  
  const handleSetSearchTerm = (searchTerm) => {
    dispatch(setSearchTerm(searchTerm));
  };
  
  const handleSetSortBy = (sortBy) => {
    dispatch(setSortBy(sortBy));
  };
  
  const handleSetCategoryFilter = (category) => {
    dispatch(setCategoryFilter(category));
  };
  
  // Multi-select actions
  const handleToggleMultiSelectMode = () => {
    dispatch(toggleMultiSelectMode());
  };
  
  const handleToggleItemSelection = (itemId) => {
    dispatch(toggleItemSelection(itemId));
  };
  
  const handleSelectAllItems = () => {
    const allItemIds = clothingItems.map(item => item.id);
    dispatch(selectAllItems(allItemIds));
  };
  
  const handleClearSelections = () => {
    dispatch(clearSelections());
  };
  
  // API actions with UI state cleanup
  const handleUploadClothing = async ({ file, clothesName, size }) => {
    try {
      // The mutation expects { file, clothesName, size }
      const result = await uploadClothing({ file, clothesName, size }).unwrap();
      return result;
    } catch (error) {
      console.error('Failed to upload clothing:', error);
      throw error;
    }
  };
  
  const handleDeleteClothing = async (itemId) => {
    try {
      await deleteClothing(itemId).unwrap();
      // Clean up UI state
      dispatch(cleanupDeletedItems(itemId));
      return true;
    } catch (error) {
      console.error('Failed to delete clothing:', error);
      throw error;
    }
  };
  
  const handleDeleteMultipleClothing = async (itemIds) => {
    try {
      // Delete items in parallel
      await Promise.all(itemIds.map(id => deleteClothing(id).unwrap()));
      // Clean up UI state
      dispatch(cleanupDeletedItems(itemIds));
      dispatch(clearSelections());
      return true;
    } catch (error) {
      console.error('Failed to delete multiple clothing items:', error);
      throw error;
    }
  };
  
  const handleUpdateClothing = async (updates) => {
    try {
      // The mutation expects { id, ...updates }
      const result = await updateClothing(updates).unwrap();
      return result;
    } catch (error) {
      console.error('Failed to update clothing:', error);
      throw error;
    }
  };
  
  // Helper functions
  const getItemById = (itemId) => {
    // Convert itemId to number for comparison if needed
    const numericId = typeof itemId === 'string' ? parseInt(itemId, 10) : itemId;
    return clothingItems.find(item => {
      const itemIdNum = typeof item.id === 'string' ? parseInt(item.id, 10) : item.id;
      return itemIdNum === numericId || item.id === itemId;
    });
  };
  
  const isItemSelectedForTryOn = (itemId) => {
    return selectedForTryOn.includes(itemId);
  };
  
  const isItemSelected = (itemId) => {
    return selectedItemIds.includes(itemId);
  };
  
  // Filter clothing items based on current filters
  const getFilteredItems = () => {
    let filtered = [...clothingItems];
    
    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        (item.clothesName && item.clothesName.toLowerCase().includes(searchLower)) ||
        (item.size && item.size.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply category filter
    if (filters.category && filters.category !== 'all') {
      // This would need to be implemented based on how categories are stored
      // For now, we'll skip this filter
    }
    
    // Apply sorting
    if (filters.sortBy) {
      const [field, direction] = filters.sortBy.split(':');
      filtered.sort((a, b) => {
        let aVal = field === 'createdAt' ? (a.addedAt || a.createdAt || a.created_at) : a[field];
        let bVal = field === 'createdAt' ? (b.addedAt || b.createdAt || b.created_at) : b[field];
        
        if (field === 'createdAt' || field === 'updatedAt') {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }
        
        if (direction === 'desc') {
          return bVal > aVal ? 1 : -1;
        } else {
          return aVal > bVal ? 1 : -1;
        }
      });
    }
    
    return filtered;
  };
  
  return {
    // Data
    clothingItems: getFilteredItems(),
    allClothingItems: clothingItems,
    selectedForTryOn,
    activeItems,
    hasActiveItems,
    filters,
    multiSelectMode,
    selectedItemIds,
    
    // Loading states
    isLoading,
    isUploading,
    isDeleting,
    isUpdating,
    error,
    
    // Actions
    addToTryOn: handleAddToTryOn,
    removeFromTryOn: handleRemoveFromTryOn,
    clearTryOnItems: handleClearTryOnItems,
    setActiveItems: handleSetActiveItems,
    
    // Filter actions
    setFilters: handleSetFilters,
    setSearchTerm: handleSetSearchTerm,
    setSortBy: handleSetSortBy,
    setCategoryFilter: handleSetCategoryFilter,
    
    // Multi-select actions
    toggleMultiSelectMode: handleToggleMultiSelectMode,
    toggleItemSelection: handleToggleItemSelection,
    selectAllItems: handleSelectAllItems,
    clearSelections: handleClearSelections,
    
    // API actions
    uploadClothing: handleUploadClothing,
    deleteClothing: handleDeleteClothing,
    deleteMultipleClothing: handleDeleteMultipleClothing,
    updateClothing: handleUpdateClothing,
    refetch: () => {
      if (isLoggedIn) {
        return refetch();
      } else {
        // Reload guest items from localStorage
        const items = JSON.parse(localStorage.getItem('guestClothes') || '[]');
        setGuestClothingItems(items);
        // console.log('🔄 Refreshed guest clothes:', items.length, 'items');
      }
    },
    
    // Helper functions
    getItemById,
    isItemSelectedForTryOn,
    isItemSelected
  };
};

export default useClothingItems;